import React from "react";
import { DataTable } from "../../../atoms/DataTable";
import { FieldLabel } from "../../../atoms/FieldLabel";
import { AlertMessage } from "../../../atoms/AlertMessage";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { Spinner } from "../../../atoms/Spinner";
import { ModalShell } from "../../../atoms/ModalShell";
import moment from "moment";
import {
  GRANT_DAYS_DEFAULT,
  GRANT_DAYS_MAX,
  GRANT_DAYS_MIN,
} from "../../../../utils/grantDuration";
import { HiCurrencyDollar } from "react-icons/hi";
import { BsFillSignStopFill } from "react-icons/bs";
import { CustomButton } from "../../../atoms/CustomButton";
import {
  PayoutAccountResponseDTO,
  BalanceResponseDTO,
  PayoutResponseDTO,
  ServicePlanDetailDTO,
  PaymentHistoryGroupedDTO,
  PlanTaxMode,
  ServiceWithPlansDTO,
  StripeTaxSettingsDTO,
} from "../../../../services/paymentModule";
import { formatPayModDate } from "../../../../services/planChange";
import { formatAmount, getBooleanBadge, getRecurrencyLabel } from "./types";
import {
  getMethodLabel,
  getStatusLabel,
  humanizeEnum,
} from "../paymod/payoutHelpers";

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Active",
  CANCELED: "Cancelled",
  ERROR: "Error",
  PENDING: "Pending",
  REFUNDED: "Refunded",
  SUCCEEDED: "Succeeded",
  UNPAID: "Unpaid",
};

const getPaymentStatusLabel = (status: string | undefined) => {
  if (!status) return "N/A";
  return PAYMENT_STATUS_LABELS[status.toUpperCase()] ?? humanizeEnum(status);
};

export type DetailModalKind =
  | "payoutAccounts"
  | "entityBalance"
  | "entityPayouts"
  | "subscriptionHistories"
  | "serviceAndPlans"
  | null;

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
  <div className="py-6 text-center">
    <p className="text-sm text-gray-600">{label}</p>
  </div>
);

const LoadingState: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-center py-6">
    <Spinner size="md" />
    <span className="ml-2 text-sm text-gray-600">{label}</span>
  </div>
);

interface DetailModalsProps {
  kind: DetailModalKind;
  onClose: () => void;
  entityName: string;

  payoutAccounts: PayoutAccountResponseDTO[] | null;
  payoutAccountsLoading: boolean;

  entityBalance: BalanceResponseDTO | null;
  entityBalanceLoading: boolean;

  entityPayouts: PayoutResponseDTO[] | null;
  entityPayoutsLoading: boolean;

  subscriptionHistories: PaymentHistoryGroupedDTO[] | null;
  subscriptionHistoriesLoading: boolean;

  serviceWithPlans: ServiceWithPlansDTO | null;
  serviceWithPlansLoading: boolean;
  onChangePlanTaxMode?: (
    planCode: string,
    taxMode: PlanTaxMode
  ) => Promise<void>;
  taxModeSavingPlanCode?: string | null;
  stripeTaxSettings?: StripeTaxSettingsDTO | null;

  onOpenPlanModal: () => void;
}

const TAX_MODE_LABELS: Record<PlanTaxMode, string> = {
  NONE: "No tax",
  INCLUSIVE: "Tax included",
  EXCLUSIVE: "Tax on top",
};

const TAX_MODES: PlanTaxMode[] = ["NONE", "INCLUSIVE", "EXCLUSIVE"];

/** Same geometry as our fields: touch target below md, 14px above, radius everywhere. */
const TAX_FIELD_CLASSES =
  "w-40 rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 " +
  "focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm";

const MODAL_TITLES: Record<Exclude<DetailModalKind, null>, string> = {
  payoutAccounts: "Payout accounts",
  entityBalance: "Entity balance",
  entityPayouts: "Entity payouts",
  subscriptionHistories: "Subscription histories",
  serviceAndPlans: "Service and plans",
};

export const EntityDetailModals: React.FC<DetailModalsProps> = ({
  kind,
  onClose,
  entityName,
  payoutAccounts,
  payoutAccountsLoading,
  entityBalance,
  entityBalanceLoading,
  entityPayouts,
  entityPayoutsLoading,
  subscriptionHistories,
  subscriptionHistoriesLoading,
  serviceWithPlans,
  serviceWithPlansLoading,
  onChangePlanTaxMode,
  taxModeSavingPlanCode,
  stripeTaxSettings,
  onOpenPlanModal,
}) => {
  return (
    <ModalShell
      show={kind !== null}
      onClose={onClose}
      size="4xl"
      title={kind ? MODAL_TITLES[kind] : ""}
      subtitle={entityName}
      footer={
        <CustomButton onClick={onClose} variant="secondary" title="Close" />
      }
    >
      {kind === "payoutAccounts" &&
        (payoutAccountsLoading ? (
          <LoadingState label="Loading payout accounts..." />
        ) : !payoutAccounts || payoutAccounts.length === 0 ? (
          <EmptyState label="No payout accounts found for this entity." />
        ) : (
          <div className="space-y-3">
            {payoutAccounts.map((account, index) => (
              <div
                key={index}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Payment method</p>
                    <StatusBadge color="gray">
                      {getMethodLabel(account.payout_method)}
                    </StatusBadge>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Status</p>
                    <StatusBadge
                      color={
                        account.status === "ACTIVE"
                          ? "success"
                          : account.status === "PENDING"
                          ? "warning"
                          : "gray"
                      }
                    >
                      {getStatusLabel(account.status)}
                    </StatusBadge>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Default</p>
                    {getBooleanBadge(account.is_default)}
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Created</p>
                    <p className="text-sm tabular-nums text-gray-900">
                      {formatPayModDate(
                        account.creation_date_time,
                        "lll",
                        "N/A"
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Last updated</p>
                    <p className="text-sm tabular-nums text-gray-900">
                      {formatPayModDate(account.update_date_time, "lll", "N/A")}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Fee rate</p>
                    <p className="text-sm tabular-nums text-gray-900">
                      {account.fee_rate / 100}%
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Fixed fee</p>
                    <p className="text-sm tabular-nums text-gray-900">
                      {(account.fixed_fee / 100).toFixed(2)}
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    <p className="mb-1 text-xs text-gray-500">
                      Account details
                    </p>
                    <div className="rounded bg-gray-50 p-2 font-mono text-sm text-gray-900">
                      {account.payout_method === "STRIPE_CONNECT" &&
                        "stripeAccountId" in account.account_detail && (
                          <p>
                            Account ID: {account.account_detail.stripeAccountId}
                          </p>
                        )}
                      {account.payout_method === "BANK_TRANSFER" &&
                        "iban" in account.account_detail && (
                          <>
                            <p>IBAN: {account.account_detail.iban}</p>
                            <p>
                              Account holder:{" "}
                              {account.account_detail.account_holder}
                            </p>
                            {account.account_detail.bank_name && (
                              <p>Bank: {account.account_detail.bank_name}</p>
                            )}
                            {account.account_detail.bic && (
                              <p>BIC: {account.account_detail.bic}</p>
                            )}
                          </>
                        )}
                      {account.payout_method === "CRYPTO" &&
                        "wallet_address" in account.account_detail && (
                          <>
                            <p>
                              Wallet: {account.account_detail.wallet_address}
                            </p>
                            {account.account_detail.network && (
                              <p>Network: {account.account_detail.network}</p>
                            )}
                            {account.account_detail.currency && (
                              <p>Currency: {account.account_detail.currency}</p>
                            )}
                          </>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

      {kind === "entityBalance" &&
        (entityBalanceLoading ? (
          <LoadingState label="Loading entity balance..." />
        ) : !entityBalance || entityBalance.balances.length === 0 ? (
          <EmptyState label="No balance information available for this entity." />
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border border-green-100 bg-white p-4 shadow-sm">
              <p className="mb-1 text-xs text-gray-500">Unpaid payments</p>
              <p className="text-xl font-semibold tabular-nums text-gray-900">
                {entityBalance.unpaid_payments_count}
              </p>
            </div>
            {entityBalance.balances.map((balance, index) => (
              <div
                key={index}
                className="rounded-lg border border-green-100 bg-white p-4 shadow-sm"
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Currency</p>
                    <StatusBadge color="gray">{balance.currency}</StatusBadge>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Gross amount</p>
                    <p className="text-base font-semibold tabular-nums text-gray-900">
                      {formatAmount(
                        balance.gross_amount / 100,
                        balance.currency
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Estimated fee</p>
                    <p className="text-base font-semibold tabular-nums text-gray-900">
                      {formatAmount(
                        balance.estimated_fee / 100,
                        balance.currency
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">
                      Estimated net amount
                    </p>
                    <p className="text-base font-semibold tabular-nums text-gray-900">
                      {formatAmount(
                        balance.estimated_net_amount / 100,
                        balance.currency
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

      {kind === "entityPayouts" &&
        (entityPayoutsLoading ? (
          <LoadingState label="Loading entity payouts..." />
        ) : !entityPayouts || entityPayouts.length === 0 ? (
          <EmptyState label="No payouts found for this entity." />
        ) : (
          <div className="space-y-3">
            {entityPayouts.map((payout, index) => (
              <div
                key={index}
                className="rounded-lg border border-amber-100 bg-white p-4 shadow-sm"
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Payout ID</p>
                    <p className="font-mono text-xs text-gray-900">
                      {payout.payout_identification}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Currency</p>
                    <StatusBadge color="gray">{payout.currency}</StatusBadge>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Gross amount</p>
                    <p className="text-base font-semibold tabular-nums text-gray-900">
                      {formatAmount(payout.gross_amount / 100, payout.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Platform fee</p>
                    <p className="text-sm tabular-nums text-gray-900">
                      {formatAmount(payout.platform_fee / 100, payout.currency)}{" "}
                      ({payout.platform_fee_rate / 100}%)
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Net amount</p>
                    <p className="text-base font-semibold tabular-nums text-gray-900">
                      {formatAmount(payout.net_amount / 100, payout.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Status</p>
                    <StatusBadge
                      color={
                        payout.status === "PAID"
                          ? "success"
                          : payout.status === "PENDING"
                          ? "warning"
                          : payout.status === "PROCESSING"
                          ? "info"
                          : payout.status === "FAILED"
                          ? "failure"
                          : "gray"
                      }
                    >
                      {getStatusLabel(payout.status)}
                    </StatusBadge>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Payout method</p>
                    <StatusBadge color="gray">
                      {getMethodLabel(payout.payout_method)}
                    </StatusBadge>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Period</p>
                    <p className="text-xs tabular-nums text-gray-900">
                      {moment(payout.period_start).format("L")} -{" "}
                      {moment(payout.period_end).format("L")}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Payout date</p>
                    <p className="text-sm tabular-nums text-gray-900">
                      {formatPayModDate(payout.payout_date_time, "lll", "N/A")}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Creation date</p>
                    <p className="text-sm tabular-nums text-gray-900">
                      {formatPayModDate(
                        payout.creation_date_time,
                        "lll",
                        "N/A"
                      )}
                    </p>
                  </div>
                  {payout.provider_reference && (
                    <div className="md:col-span-4">
                      <p className="mb-1 text-xs text-gray-500">
                        Transaction reference
                      </p>
                      <p className="font-mono text-xs text-gray-900">
                        {payout.provider_reference}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}

      {kind === "subscriptionHistories" &&
        (subscriptionHistoriesLoading ? (
          <LoadingState label="Loading subscription histories..." />
        ) : !subscriptionHistories || subscriptionHistories.length === 0 ? (
          <EmptyState label="No subscription histories found for this entity." />
        ) : (
          <div className="space-y-4">
            {subscriptionHistories.map((history, historyIndex) => (
              <div
                key={historyIndex}
                className="rounded-lg border border-blue-100 bg-white p-4 shadow-sm"
              >
                <div className="mb-3 border-b border-blue-100 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {history.planDescription}
                      </p>
                      <p className="mt-1 font-mono text-xs text-gray-500">
                        {history.planCode}
                      </p>
                    </div>
                    <StatusBadge color="gray">
                      {history.paymentSteps.length} payments
                    </StatusBadge>
                  </div>
                </div>
                <div className="space-y-3">
                  {history.paymentSteps.map((step, stepIndex) => (
                    <div
                      key={stepIndex}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                    >
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div>
                          <p className="mb-1 text-xs text-gray-500">
                            Payment date
                          </p>
                          <p className="text-sm tabular-nums text-gray-900">
                            {formatPayModDate(
                              step.paymentDateTime,
                              "lll",
                              "N/A"
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="mb-1 text-xs text-gray-500">
                            Active until
                          </p>
                          <p className="text-sm tabular-nums text-gray-900">
                            {step.paymentNextDate
                              ? moment(step.paymentNextDate).format("L")
                              : "No payment planned"}
                          </p>
                        </div>
                        <div>
                          <p className="mb-1 text-xs text-gray-500">Status</p>
                          <StatusBadge
                            color={
                              step.paymentStatusEnum === "SUCCEEDED"
                                ? "success"
                                : step.paymentStatusEnum === "ACTIVE"
                                ? "info"
                                : step.paymentStatusEnum === "UNPAID"
                                ? "warning"
                                : step.paymentStatusEnum === "ERROR" ||
                                  step.paymentStatusEnum === "CANCELED"
                                ? "failure"
                                : "gray"
                            }
                          >
                            {getPaymentStatusLabel(step.paymentStatusEnum)}
                          </StatusBadge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

      {kind === "serviceAndPlans" &&
        (serviceWithPlansLoading ? (
          <LoadingState label="Loading service information..." />
        ) : !serviceWithPlans ? (
          <EmptyState label="No service declared for this entity." />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="mb-1 text-xs text-gray-500">Service name</p>
                <p className="text-sm font-semibold text-gray-900">
                  {serviceWithPlans.service_name}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs text-gray-500">Description</p>
                <p className="text-sm text-gray-900">
                  {serviceWithPlans.service_description || "N/A"}
                </p>
              </div>
              {serviceWithPlans.stripe_product_id && (
                <div>
                  <p className="mb-1 text-xs text-gray-500">
                    Stripe product ID
                  </p>
                  <p className="font-mono text-xs text-gray-900">
                    {serviceWithPlans.stripe_product_id}
                  </p>
                </div>
              )}
              <div>
                <p className="mb-1 text-xs text-gray-500">Stripe tax code</p>
                <p className="font-mono text-xs text-gray-900">
                  {serviceWithPlans.service_stripe_tax_code || "Stripe default"}
                </p>
              </div>
              {serviceWithPlans.created_at && (
                <div>
                  <p className="mb-1 text-xs text-gray-500">Created at</p>
                  <p className="text-sm tabular-nums text-gray-900">
                    {formatPayModDate(serviceWithPlans.created_at, "L")}
                  </p>
                </div>
              )}
            </div>

            <div>
              <h5 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
                <HiCurrencyDollar className="text-gray-500" />
                Plans ({serviceWithPlans.plans?.length || 0})
              </h5>

              {stripeTaxSettings &&
                stripeTaxSettings.registration_countries.length === 0 && (
                  <AlertMessage color="warning" className="mb-3">
                    <span className="font-medium">
                      No tax registration on this Stripe account.
                    </span>{" "}
                    Whatever a plan is set to below, Stripe computes zero. Add
                    the registrations in Stripe before counting on anything
                    being collected.
                  </AlertMessage>
                )}
              {!serviceWithPlans.plans ||
              serviceWithPlans.plans.length === 0 ? (
                <p className="text-sm text-gray-500">No plans found.</p>
              ) : (
                <DataTable>
                  <DataTable.Head>
                    <DataTable.HeadCell>Plan code</DataTable.HeadCell>
                    <DataTable.HeadCell>Amount</DataTable.HeadCell>
                    <DataTable.HeadCell>Recurrence</DataTable.HeadCell>
                    <DataTable.HeadCell>Offered</DataTable.HeadCell>
                    <DataTable.HeadCell>Tax</DataTable.HeadCell>
                  </DataTable.Head>
                  <DataTable.Body>
                    {serviceWithPlans.plans.map((plan) => (
                      <DataTable.Row
                        key={plan.plan_code}
                        className={plan.is_public ? "bg-white" : "bg-gray-50"}
                      >
                        <DataTable.Cell className="max-w-[220px]">
                          <div
                            className="truncate font-mono text-xs font-medium text-gray-900"
                            title={plan.plan_code}
                          >
                            {plan.plan_code}
                          </div>
                          {plan.plan_description && (
                            <div
                              className="truncate text-xs text-gray-500"
                              title={plan.plan_description}
                            >
                              {plan.plan_description}
                            </div>
                          )}
                        </DataTable.Cell>
                        <DataTable.Cell className="whitespace-nowrap font-semibold tabular-nums">
                          {formatAmount(plan.amount / 100, plan.currency)}
                        </DataTable.Cell>
                        <DataTable.Cell>
                          <StatusBadge color="info">
                            {getRecurrencyLabel(plan.recurrence)}
                          </StatusBadge>
                        </DataTable.Cell>
                        <DataTable.Cell>
                          <StatusBadge
                            color={plan.is_public ? "success" : "gray"}
                          >
                            {plan.is_public ? "Yes" : "No"}
                          </StatusBadge>
                        </DataTable.Cell>
                        <DataTable.Cell>
                          {onChangePlanTaxMode &&
                          plan.tax_mode_settable !== false ? (
                            <>
                              <select
                                aria-label={`Tax mode of ${plan.plan_code}`}
                                className={TAX_FIELD_CLASSES}
                                value={plan.tax_mode}
                                disabled={
                                  taxModeSavingPlanCode === plan.plan_code
                                }
                                onChange={(event) =>
                                  onChangePlanTaxMode(
                                    plan.plan_code,
                                    event.target.value as PlanTaxMode
                                  )
                                }
                              >
                                {TAX_MODES.map((mode) => (
                                  <option key={mode} value={mode}>
                                    {TAX_MODE_LABELS[mode]}
                                  </option>
                                ))}
                              </select>
                            </>
                          ) : (
                            <div>
                              <span className="text-xs text-gray-500">
                                {TAX_MODE_LABELS[plan.tax_mode]}
                              </span>
                              {plan.tax_mode_settable === false && (
                                <p className="text-xs text-gray-400">
                                  {plan.provider_plan_identification ===
                                  "dynamic_price"
                                    ? "Merchant payment link, never taxed"
                                    : "Not a Stripe plan"}
                                </p>
                              )}
                            </div>
                          )}
                        </DataTable.Cell>
                      </DataTable.Row>
                    ))}
                  </DataTable.Body>
                </DataTable>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4">
              <CustomButton
                onClick={onOpenPlanModal}
                variant="primary"
                title="Manage private plans (grant / expire)"
                icon="handsHelping"
              />
            </div>
          </div>
        ))}
    </ModalShell>
  );
};

interface PlanManagementModalProps {
  show: boolean;
  onClose: () => void;
  entityName?: string;
  entityId?: string;

  privatePlans: ServicePlanDetailDTO[];
  privatePlansLoading: boolean;

  selectedPlan: string;
  onSelectedPlanChange: (v: string) => void;
  durationDays: number;
  onDurationDaysChange: (v: number) => void;
  grantingPlan: boolean;
  onGrantPlan: () => void;

  planToExpire: string;
  onPlanToExpireChange: (v: string) => void;
  expiringPlan: boolean;
  onExpirePlanClick: () => void;
}

export const PlanManagementModal: React.FC<PlanManagementModalProps> = ({
  show,
  onClose,
  entityName,
  entityId,
  privatePlans,
  privatePlansLoading,
  selectedPlan,
  onSelectedPlanChange,
  durationDays,
  onDurationDaysChange,
  grantingPlan,
  onGrantPlan,
  planToExpire,
  onPlanToExpireChange,
  expiringPlan,
  onExpirePlanClick,
}) => {
  const grantablePlans = privatePlans.filter((plan) => plan.grantable);

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="2xl"
      dismissible
      title="Grant access"
      subtitle={`${entityName} (${entityId})`}
      footer={
        <CustomButton onClick={onClose} variant="secondary" title="Close" />
      }
    >
      <div className="space-y-6">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
          <h4 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
            Grant with a private plan
          </h4>

          {privatePlansLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
              <span className="ml-2 text-sm text-gray-600">
                Loading private plans...
              </span>
            </div>
          ) : grantablePlans.length === 0 ? (
            <AlertMessage color="info">
              <span className="text-sm">
                No private plans available, or they could not be loaded.
              </span>
            </AlertMessage>
          ) : (
            <div className="space-y-4">
              <div>
                <FieldLabel
                  htmlFor="plan-select"
                  value="Select a private plan"
                />
                <select
                  id="plan-select"
                  value={selectedPlan}
                  onChange={(e) => onSelectedPlanChange(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm"
                >
                  <option value="">-- Select a plan --</option>
                  {grantablePlans.map((plan) => (
                    <option key={plan.plan_code} value={plan.plan_code}>
                      {plan.plan_description} ({plan.plan_code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <FieldLabel
                  htmlFor="grant-duration-days"
                  value="Access duration (days)"
                />
                <input
                  id="grant-duration-days"
                  type="number"
                  min={GRANT_DAYS_MIN}
                  max={GRANT_DAYS_MAX}
                  step="1"
                  value={durationDays}
                  onChange={(e) =>
                    onDurationDaysChange(parseInt(e.target.value) || 1)
                  }
                  placeholder={String(GRANT_DAYS_DEFAULT)}
                  className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {`From ${GRANT_DAYS_MIN} to ${GRANT_DAYS_MAX} days. Access runs through ${moment()
                    .add(durationDays, "days")
                    .format("L")}.`}
                </p>
              </div>

              <CustomButton
                onClick={onGrantPlan}
                disabled={!selectedPlan || grantingPlan}
                variant="primary"
                title="Grant plan"
                isLoading={grantingPlan}
              />
            </div>
          )}
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-sm text-gray-500">OR</span>
          </div>
        </div>

        <div className="rounded-lg border border-red-200 bg-red-50 p-5">
          <h4 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
            <BsFillSignStopFill className="text-red-600" size={26} />
            Force expire plan
          </h4>

          <div className="space-y-4">
            <div>
              <FieldLabel
                htmlFor="plan-to-expire"
                value="Select the plan to expire"
              />
              {privatePlansLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Spinner size="sm" />
                  <span className="ml-2 text-xs text-gray-600">
                    Loading plans...
                  </span>
                </div>
              ) : grantablePlans.length === 0 ? (
                <AlertMessage color="info">
                  <span className="text-sm">
                    No private plans available, or they could not be loaded.
                  </span>
                </AlertMessage>
              ) : (
                <select
                  id="plan-to-expire"
                  value={planToExpire}
                  onChange={(e) => onPlanToExpireChange(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm"
                >
                  <option value="">-- Select a plan to expire --</option>
                  {grantablePlans.map((plan) => (
                    <option key={plan.plan_code} value={plan.plan_code}>
                      {plan.plan_description} ({plan.plan_code})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <CustomButton
              onClick={onExpirePlanClick}
              disabled={!planToExpire || expiringPlan}
              variant="danger"
              title="Force expire plan"
              isLoading={expiringPlan}
            />
          </div>
        </div>
      </div>
    </ModalShell>
  );
};
